import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { humanise, money } from '../lib/format.js'
import {
  Badge,
  Card,
  Empty,
  Field,
  KeyValue,
  Notice,
  PageHead,
  RowsSkeleton,
  SettingRow,
  Stat,
  Switch,
  Ticks,
} from '../components/ui.js'
import {
  BellIcon,
  BoltIcon,
  CartIcon,
  ExternalIcon,
  ChatIcon,
  RupeeIcon,
  ShieldIcon,
  StoreIcon,
  WhatsAppIcon,
} from '../components/icons.js'

// ── Abandoned checkouts ─────────────────────────────────────────────────────

// The recovery screen grew into its own module: filters, bulk campaigns,
// automation and analytics. See `./abandoned/`.
export { AbandonedCheckoutsPage } from './abandoned/AbandonedCheckouts.js'

// ── WhatsApp ────────────────────────────────────────────────────────────────

/** What `/v1/whatsapp` answers with. */
interface WhatsAppStatus {
  sender: 'platform' | 'own'
  connected: boolean
  reachable: boolean
  reason: string | null
  workspace: { name: string | null; slug: string | null } | null
  wallet: { balance: string; currency: string; isLow: boolean } | null
  templates: Array<{ id: string; name: string; language: string | null; status: string; category: string | null }>
}

/**
 * Wakit's template vocabulary, rendered.
 *
 * Meta's own statuses come through Wakit already mapped — see
 * `app/Enums/TemplateStatus.php` there. Re-deriving the labels here would drift
 * the day Meta adds a state.
 */
const TEMPLATE_TONE: Record<string, string> = {
  approved: 'ok',
  submitted: 'warn',
  pending_review: 'warn',
  pending: 'warn',
  paused: 'warn',
  rejected: 'bad',
  declined: 'bad',
}

const TEMPLATE_LABEL: Record<string, string> = {
  approved: 'Approved',
  submitted: 'With WhatsApp',
  pending_review: 'Waiting for Wakit',
  pending: 'Waiting for Wakit',
  paused: 'Paused by WhatsApp',
  rejected: 'Rejected by WhatsApp',
  declined: 'Declined by Wakit',
}

/**
 * A status window and a door — not a second WhatsApp console.
 *
 * Verifying the business, adding a payment method on Meta, getting a template
 * approved: all of that happens in Wakit, because that is where the Embedded
 * Signup session and Meta's credentials live. Rebuilding those screens here
 * would mean holding a second copy of a state Meta owns.
 *
 * So this reads the status and hands out a one-time SSO link for the doing.
 */
export function WhatsAppPage(): ReactNode {
  const { shop } = useAuth()
  const [ssoError, setSsoError] = useState<string | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['whatsapp', shop?.id],
    queryFn: () => api<WhatsAppStatus>('/v1/whatsapp'),
    enabled: Boolean(shop?.id),
  })

  // Fetched on click, never rendered into the page: Wakit's token expires in
  // about two minutes, so a link baked into the HTML would be dead by the time
  // anyone pressed it.
  const openWakit = useMutation({
    mutationFn: () => api<{ url: string; expiresIn: number }>('/v1/whatsapp/sso', { method: 'POST' }),
    onSuccess: (result) => {
      setSsoError(null)
      if (result.url) window.open(result.url, '_blank', 'noopener')
    },
    onError: (err) => setSsoError((err as Error).message),
  })

  const queryClient = useQueryClient()
  const [wakitKey, setWakitKey] = useState('')

  const connectWakit = useMutation({
    mutationFn: () => api<{ connected: boolean }>('/v1/whatsapp/connect', { method: 'POST', body: { apiKey: wakitKey.trim() } }),
    onSuccess: () => {
      setWakitKey('')
      void queryClient.invalidateQueries({ queryKey: ['whatsapp', shop?.id] })
    },
  })

  const disconnectWakit = useMutation({
    mutationFn: () => api<{ connected: boolean }>('/v1/whatsapp/connect', { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['whatsapp', shop?.id] }),
  })

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          WhatsApp is configured per store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  const awaiting = data?.templates.filter((template) => template.status !== 'approved') ?? []

  return (
    <>
      <PageHead
        title="WhatsApp"
        subtitle="Order updates and cart recovery, delivered through Wakit."
        crumbs={[{ label: 'Engagement' }, { label: 'WhatsApp' }]}
        actions={
          data?.sender === 'own' ? (
            <button type="button" className="btn btn--primary" disabled={openWakit.isPending} onClick={() => openWakit.mutate()}>
              <ExternalIcon size={16} />
              {openWakit.isPending ? 'Opening…' : 'Open Wakit'}
            </button>
          ) : undefined
        }
      />

      {ssoError && (
        <Notice kind="error" title="Could not open Wakit">
          {ssoError}
        </Notice>
      )}

      {error && (
        <Card>
          <Empty error title="Could not load your WhatsApp status">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && (
        <Card>
          <RowsSkeleton rows={5} />
        </Card>
      )}

      {/* Wakit being unreachable is not the same as being disconnected, and a
          merchant told "not connected" will go and reconnect something fine. */}
      {data && !data.reachable && data.connected && (
        <Notice kind="warn" title="Wakit could not be reached">
          {data.reason ?? 'Your connection is unchanged — this panel just could not read its status.'}
        </Notice>
      )}

      {data && (
        <div className="grid grid--main">
          <div className="stack">
            {data.sender === 'platform' ? (
              <Card title="Your sender" subtitle="Messages go out from Outfalak Checkout.">
                <div className="row" style={{ marginBottom: 14 }}>
                  <span className="feature__icon" style={{ ['--tone' as string]: 'var(--brand)', width: 44, height: 44 }}>
                    <BoltIcon size={22} />
                  </span>
                  <span className="list__text">
                    <strong>Outfalak Checkout</strong>
                    <small>Our verified WhatsApp Business number</small>
                  </span>
                  <Badge tone="ok" dot>
                    Active
                  </Badge>
                </div>

                <p className="muted" style={{ fontSize: '.87rem' }}>
                  Nothing to set up and no template approvals to wait on. Order confirmations and cart reminders go out from our
                  number, and shoppers see Outfalak Checkout as the sender.
                </p>

                <Notice kind="info" title="Have a Wakit account?">
                  Connect it and your order messages go out from <strong>your</strong> number, billed to your Wakit wallet.
                  Copy the API key from Wakit and paste it here.
                </Notice>

                {connectWakit.error && (
                  <Notice kind="error" title="Could not connect">
                    {(connectWakit.error as Error).message}
                  </Notice>
                )}

                <Field label="Wakit API key">
                  <input
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    value={wakitKey}
                    placeholder="Paste your Wakit API key"
                    onChange={(event) => setWakitKey(event.target.value)}
                  />
                </Field>

                <button
                  type="button"
                  className="btn btn--primary btn--block"
                  disabled={wakitKey.trim().length < 8 || connectWakit.isPending}
                  onClick={() => connectWakit.mutate()}
                >
                  {connectWakit.isPending ? 'Checking the key…' : 'Connect Wakit'}
                </button>
              </Card>
            ) : (
              <>
                <Card
                  title="Your WhatsApp Business account"
                  action={
                    <Badge tone={data.connected ? 'connected' : 'disconnected'} dot>
                      {data.connected ? 'Connected' : 'Not connected'}
                    </Badge>
                  }
                >
                  <div className="row" style={{ marginBottom: 16 }}>
                    <span className="feature__icon" style={{ ['--tone' as string]: 'var(--ok)', width: 44, height: 44 }}>
                      <WhatsAppIcon size={22} />
                    </span>
                    <span className="list__text">
                      <strong>{data.workspace?.name ?? 'Your workspace'}</strong>
                      <small>Managed in Wakit</small>
                    </span>
                  </div>

                  {data.wallet && (
                    <KeyValue
                      rows={[
                        { label: 'Wakit workspace', value: data.workspace?.slug ?? '—' },
                        {
                          label: 'Message balance',
                          value: (
                            <>
                              {data.wallet.balance}
                              {data.wallet.isLow && (
                                <>
                                  {' '}
                                  <Badge tone="warn">Low</Badge>
                                </>
                              )}
                            </>
                          ),
                        },
                      ]}
                    />
                  )}

                  {data.wallet?.isLow && (
                    <Notice kind="warn" title="Message balance is low">
                      Messages stop sending when it runs out. Top up in Wakit.
                    </Notice>
                  )}

                  <button
                    type="button"
                    className="btn btn--block"
                    style={{ marginTop: 16 }}
                    disabled={openWakit.isPending}
                    onClick={() => openWakit.mutate()}
                  >
                    <ExternalIcon size={16} />
                    Manage in Wakit
                  </button>

                  <button
                    type="button"
                    className="btn btn--block"
                    style={{ marginTop: 8 }}
                    disabled={disconnectWakit.isPending}
                    onClick={() => {
                      if (window.confirm('Disconnect Wakit? Messages go back to the Outfalak Checkout sender.')) {
                        disconnectWakit.mutate()
                      }
                    }}
                  >
                    {disconnectWakit.isPending ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </Card>

                {/*
                  Verification and template approval both live on Meta, reached
                  through Wakit. Outfalak states what needs doing and opens the
                  door; it does not pretend to be the place it gets done.
                */}
                <Card title="Business verification" subtitle="Done on Meta, through Wakit.">
                  <p className="muted" style={{ fontSize: '.87rem', marginBottom: 14 }}>
                    Until Meta verifies your business, the account stays on unverified limits — a small number of
                    business-initiated conversations a day, and a cap on how many numbers it can hold. Meta accepts verification
                    documents only in its own Security Centre, never here.
                  </p>

                  <button type="button" className="btn btn--block" disabled={openWakit.isPending} onClick={() => openWakit.mutate()}>
                    <ShieldIcon size={16} />
                    Verify your business in Wakit
                    <ExternalIcon size={14} />
                  </button>
                </Card>
              </>
            )}

            <Card
              title="Templates"
              subtitle={
                data.sender === 'own'
                  ? 'Approved by WhatsApp before they can send. Managed in Wakit.'
                  : 'Sent from the Outfalak Checkout sender — already approved.'
              }
              flush
            >
              {data.sender === 'platform' ? (
                <div className="card__body">
                  <p className="muted" style={{ fontSize: '.87rem' }}>
                    Order confirmation and cart recovery are sent from our approved templates. There is nothing to submit.
                  </p>
                </div>
              ) : data.templates.length === 0 ? (
                <Empty icon={<ChatIcon size={22} />} title="No templates yet">
                  Templates assigned to your workspace appear here. Create one in Wakit and it shows up after the next refresh.
                </Empty>
              ) : (
                <ul className="list">
                  {data.templates.map((template) => (
                    <li key={template.id}>
                      <span className="feature__icon" style={{ ['--tone' as string]: 'var(--ok)' }}>
                        <ChatIcon size={16} />
                      </span>
                      <span className="list__text">
                        <strong>{template.name}</strong>
                        <small>
                          {template.category ? `${template.category} · ` : ''}
                          {template.language ?? 'en'}
                        </small>
                      </span>
                      <Badge tone={TEMPLATE_TONE[template.status] ?? undefined}>
                        {TEMPLATE_LABEL[template.status] ?? humanise(template.status)}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {data.sender === 'own' && awaiting.length > 0 && (
              <Notice kind="info" title={`${awaiting.length} template still under review`}>
                Until WhatsApp approves it, that message falls back to the Outfalak Checkout sender — nothing stops sending while
                you wait.
              </Notice>
            )}
          </div>

          <div className="stack">
            {/* OTP is not a merchant template and has no switch — saying so
                stops its absence from the list reading as a gap. */}
            <Card title="Phone verification (OTP)">
              <div className="row" style={{ marginBottom: 14 }}>
                <span className="feature__icon" style={{ ['--tone' as string]: 'var(--brand)', width: 40, height: 40 }}>
                  <ShieldIcon size={20} />
                </span>
                <span className="list__text">
                  <strong>Sent by Outfalak Checkout</strong>
                  <small>Always from our sender</small>
                </span>
              </div>

              <p className="muted" style={{ fontSize: '.85rem' }}>
                When a shopper verifies their number at checkout, the code comes from our sender — never yours. That stays true
                even after you connect your own number.
              </p>
            </Card>

            <Card title="What happens in Wakit" accent>
              <Ticks
                brand
                items={[
                  'Connecting your WhatsApp Business number',
                  'Meta business verification and payment method',
                  'Writing templates and submitting them for approval',
                  'Topping up your message balance',
                ]}
              />
              {data.sender === 'own' && (
                <button
                  type="button"
                  className="btn btn--primary btn--block"
                  style={{ marginTop: 14 }}
                  disabled={openWakit.isPending}
                  onClick={() => openWakit.mutate()}
                >
                  <ExternalIcon size={16} />
                  Open Wakit
                </button>
              )}
            </Card>

            <Card title="Campaigns">
              <p className="muted" style={{ fontSize: '.87rem', marginBottom: 14 }}>
                Send a recovery message to shoppers who left a cart behind. Outfalak picks the recipients; Wakit delivers.
              </p>
              <Link to="/engagement/abandoned" className="btn btn--block">
                <CartIcon size={16} />
                Abandoned checkouts
              </Link>
            </Card>
          </div>
        </div>
      )}
    </>
  )
}
// ── Notifications ───────────────────────────────────────────────────────────

/** What `/v1/notifications` reads and writes. */
interface NotificationEvent {
  key: 'order_placed' | 'order_pending' | 'order_abandoned' | 'refund_issued'
  label: string
  description: string
  enabled: boolean
  template: string
  price: number
  sent: number
  failed: number
  queued: number
  skipped: number
}

interface NotificationConfig {
  enabled: boolean
  templateLang: string
  pendingAfterMinutes: number
  events: NotificationEvent[]
}

/**
 * Configuration is live; delivery counts are honestly zero.
 *
 * The settings here are the ones the notifier reads, so editing them now is the
 * real thing. The sender is live too — but nothing raises an event yet, so no
 * message has been queued and the counts are zero because nothing has been
 * sent, not because the screen cannot see them. Saying that plainly beats a
 * merchant concluding their messages are failing silently.
 */
export function NotificationsPage(): ReactNode {
  const { shop } = useAuth()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<NotificationConfig | null>(null)
  const [saved, setSaved] = useState(false)

  const query = useQuery({
    queryKey: ['notifications', shop?.id],
    queryFn: () => api<NotificationConfig>('/v1/notifications'),
    enabled: Boolean(shop?.id),
  })

  const save = useMutation({
    mutationFn: (body: NotificationConfig) =>
      api<NotificationConfig>('/v1/notifications', {
        method: 'PUT',
        body: {
          enabled: body.enabled,
          templateLang: body.templateLang,
          pendingAfterMinutes: body.pendingAfterMinutes,
          events: body.events.map((event) => ({ key: event.key, enabled: event.enabled, template: event.template })),
        },
      }),
    onSuccess: (next) => {
      setDraft(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['notifications', shop?.id] })
    },
  })

  /*
   * The platform sender's templates, fetched live from Wakit for the inputs
   * below. Typing blind is how a merchant spends a week on a name Wakit never
   * approved; the suggestions are the approved list, and anything outside it
   * gets a warning before Save rather than a failure after.
   */
  const templatesQuery = useQuery({
    queryKey: ['notification-templates', shop?.id],
    queryFn: () =>
      api<{ configured: boolean; templates: Array<{ name: string; language: string | null; status: string }>; error: string | null }>(
        '/v1/notifications/templates',
      ),
    enabled: Boolean(shop?.id),
    staleTime: 300_000,
  })

  const approvedTemplates = useMemo(
    () => (templatesQuery.data?.templates ?? []).filter((row) => row.status.toLowerCase() === 'approved'),
    [templatesQuery.data],
  )

  const current = draft ?? query.data ?? null
  const dirty = draft !== null && query.data !== undefined && JSON.stringify(draft) !== JSON.stringify(query.data)

  function setEvent(key: NotificationEvent['key'], patch: Partial<NotificationEvent>): void {
    setDraft((previous) => {
      const base = previous ?? query.data
      if (!base) return null
      return { ...base, events: base.events.map((event) => (event.key === key ? { ...event, ...patch } : event)) }
    })
  }

  function setGlobal<K extends keyof NotificationConfig>(key: K, value: NotificationConfig[K]): void {
    setDraft((previous) => {
      const base = previous ?? query.data
      return base ? { ...base, [key]: value } : null
    })
  }

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Notification settings belong to a store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  const totals = current
    ? current.events.reduce(
        (sum, event) => ({
          sent: sum.sent + event.sent,
          failed: sum.failed + event.failed,
          queued: sum.queued + event.queued,
        }),
        { sent: 0, failed: 0, queued: 0 },
      )
    : null

  const activeCost = current?.events.filter((event) => event.enabled).reduce((sum, event) => sum + event.price, 0) ?? 0

  return (
    <>
      <PageHead
        title="Notifications"
        subtitle="What gets sent to shoppers, and what each message costs."
        crumbs={[{ label: 'Engagement' }, { label: 'Notifications' }]}
        actions={
          <button
            type="button"
            className="btn btn--primary"
            disabled={!dirty || save.isPending}
            onClick={() => current && save.mutate(current)}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        }
      />

      {saved && <Notice kind="ok" title="Saved">These settings apply to messages from now on.</Notice>}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      {dirty && !save.isPending && (
        <Notice kind="warn" title="Unsaved changes">
          Nothing changes until you press Save.
        </Notice>
      )}

      {/* The counts below are real and zero. Without this a merchant reads the
          zeros as "my messages are failing". */}
      <Notice kind="info" title="Messages are not going out yet">
        Everything here is live — these are the settings each message is sent with. Orders are not raising messages on this
        deployment yet, so the delivery counts below stay at zero. Nothing is failing.
      </Notice>

      {query.error && (
        <Card>
          <Empty error title="Could not load notification settings">
            {(query.error as Error).message}
          </Empty>
        </Card>
      )}

      {query.isPending && (
        <Card>
          <RowsSkeleton rows={6} />
        </Card>
      )}

      {current && totals && (
        <>
          <section className="grid grid--4">
            <Stat label="Sent" value={totals.sent.toLocaleString('en-IN')} icon={<BellIcon size={20} />} tone="var(--brand)" />
            <Stat label="Failed" value={totals.failed.toLocaleString('en-IN')} icon={<BellIcon size={20} />} tone="var(--bad)" />
            <Stat label="Queued" value={totals.queued.toLocaleString('en-IN')} icon={<ChatIcon size={20} />} tone="var(--warn)" />
            <Stat
              label="Cost per order"
              value={activeCost > 0 ? money(activeCost) : '—'}
              icon={<RupeeIcon size={20} />}
              tone="var(--accent)"
              footnote={<span className="faint">if every enabled message sends</span>}
            />
          </section>

          <Card title="Messages" subtitle="Each one is billed to your wallet when it sends." flush>
            <ul className="list">
              {current.events.map((event) => (
                <li key={event.key} style={{ alignItems: 'flex-start' }}>
                  <span
                    className="feature__icon"
                    style={{ ['--tone' as string]: event.enabled ? 'var(--ok)' : 'var(--ink-faint)' }}
                  >
                    <WhatsAppIcon size={16} />
                  </span>

                  <span className="list__text">
                    <strong>{event.label}</strong>
                    <small>{event.description}</small>
                    <small>
                      <input
                        className="input"
                        list={`notification-template-${event.key}`}
                        value={event.template}
                        disabled={!current.enabled}
                        onChange={(e) => setEvent(event.key, { template: e.target.value })}
                        aria-label={`${event.label} template name`}
                        placeholder="Template name"
                        style={{ maxWidth: 240 }}
                      />
                      <datalist id={`notification-template-${event.key}`}>
                        {approvedTemplates.map((row) => (
                          <option key={`${row.name}-${row.language ?? ''}`} value={row.name}>
                            {row.language ?? ''} · {row.status}
                          </option>
                        ))}
                      </datalist>
                      {templatesQuery.data && !approvedTemplates.some((row) => row.name === event.template) && (
                        <span style={{ color: 'var(--warn)' }}> · not in the approved list — messages will fail</span>
                      )}
                      {event.sent + event.failed > 0 && (
                        <>
                          {' '}
                          · {event.sent.toLocaleString('en-IN')} sent
                          {event.failed > 0 && <span style={{ color: 'var(--bad)' }}> · {event.failed} failed</span>}
                        </>
                      )}
                    </small>
                  </span>

                  {/* Price is read-only: it is the platform's number, not the
                      merchant's, so there is no input for it. */}
                  <Badge tone={event.price > 0 ? 'warn' : undefined}>
                    {event.price > 0 ? `${money(event.price)} each` : 'Free'}
                  </Badge>

                  <Switch
                    checked={event.enabled}
                    label={event.label}
                    disabled={!current.enabled}
                    onChange={(value) => setEvent(event.key, { enabled: value })}
                  />
                </li>
              ))}
            </ul>
          </Card>

          <div className="grid grid--2">
            <Card title="Delivery">
              <SettingRow
                title="Send order notifications"
                description="The master switch. Off means nothing is sent, whatever the messages above say."
              >
                <Switch checked={current.enabled} label="Send order notifications" onChange={(v) => setGlobal('enabled', v)} />
              </SettingRow>

              <div className="stack" style={{ marginTop: 16, opacity: current.enabled ? 1 : 0.5 }}>
                <Field label="Template language" hint="WhatsApp template locale, e.g. en or en_US.">
                  <input
                    className="input"
                    value={current.templateLang}
                    disabled={!current.enabled}
                    onChange={(e) => setGlobal('templateLang', e.target.value)}
                  />
                </Field>

                <Field
                  label="Payment pending delay (minutes)"
                  hint="How long to wait after an incomplete payment before nudging the shopper."
                >
                  <input
                    className="input"
                    inputMode="numeric"
                    value={String(current.pendingAfterMinutes)}
                    disabled={!current.enabled}
                    onChange={(e) => setGlobal('pendingAfterMinutes', Number(e.target.value.replace(/\D/g, '')) || 1)}
                  />
                </Field>
              </div>
            </Card>

            <Card title="What this costs" accent>
              <p className="muted" style={{ fontSize: '.87rem', marginBottom: 12 }}>
                Notifications are billed per message, debited from your wallet when they send.
              </p>
              <KeyValue
                rows={current.events.map((event) => ({
                  label: event.label,
                  value: event.enabled ? (event.price > 0 ? money(event.price) : 'Free') : 'Off',
                }))}
              />
              <p className="muted" style={{ fontSize: '.8rem', marginTop: 12 }}>
                <BellIcon size={13} style={{ verticalAlign: '-2px' }} /> Checkout OTP is not billed here — it is sent by Outfalak
                Checkout, not from your account.
              </p>
            </Card>
          </div>
        </>
      )}
    </>
  )
}
